import React, { useState } from 'react';

import { CheckCircle, Clock, Copy, Globe, XCircle } from 'lucide-react';

import {
	useGetTenantDomainsQuery,
	useGetVerificationInstructionsQuery,
	useVerifyDomainMutation,
} from '../api/domains';
import { useToast } from '../context/ToastContext';

import {
	Alert,
	Button,
	Modal,
	ModalBody,
	ModalFooter,
	Typography,
} from './common';

import type { Domain } from '../api/domains';

interface TenantDomainsModalProps {
	isOpen: boolean;
	onClose: () => void;
	tenantId: string;
	tenantName: string;
}

const TenantDomainsModal: React.FC<TenantDomainsModalProps> = ({
	isOpen,
	onClose,
	tenantId,
	tenantName,
}) => {
	const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
	const { showToast } = useToast();

	const { data: domainsData, isLoading: domainsLoading } =
		useGetTenantDomainsQuery(tenantId);
	const { data: instructionsData, isLoading: instructionsLoading } =
		useGetVerificationInstructionsQuery(selectedDomain?.id || '', {
			skip: !selectedDomain || selectedDomain.domain_type !== 'custom',
		});

	const [verifyDomain, { isLoading: verifying }] = useVerifyDomainMutation();

	const domains = domainsData?.domains || [];

	const getStatusIcon = (status: string) => {
		switch (status) {
			case 'verified':
			case 'active':
				return <CheckCircle className="w-4 h-4 text-green-600" />;
			case 'failed':
				return <XCircle className="w-4 h-4 text-red-600" />;
			default:
				return <Clock className="w-4 h-4 text-yellow-600" />;
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'verified':
			case 'active':
				return 'text-green-600 bg-green-50';
			case 'failed':
				return 'text-red-600 bg-red-50';
			default:
				return 'text-yellow-600 bg-yellow-50';
		}
	};

	const handleCopy = (text: string) => {
		navigator.clipboard.writeText(text);
		showToast('Copied!', 'DNS record copied to clipboard');
	};

	const handleVerify = async () => {
		if (!selectedDomain) return;

		try {
			const result = await verifyDomain(selectedDomain.id).unwrap();
			if (result.success) {
				showToast('Success', 'Domain verified successfully!');
			} else {
				showToast('Verification Failed', result.message);
			}
		} catch (error: any) {
			showToast('Error', error?.data?.message || 'Failed to verify domain');
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={`Domains - ${tenantName}`}
			size="lg"
		>
			<ModalBody>
				{domainsLoading ? (
					<div className="text-center py-8">
						<Typography variant="body2" color="muted">
							Loading domains...
						</Typography>
					</div>
				) : domains.length === 0 ? (
					<Alert variant="info">No domains configured for this tenant.</Alert>
				) : (
					<div className="space-y-4">
						{/* Domains List */}
						<div>
							<Typography variant="h3" className="mb-3">
								Domains
							</Typography>
							<div className="space-y-2">
								{domains.map((domain) => (
									<div
										key={domain.id}
										className={`p-4 border rounded-lg cursor-pointer transition-colors ${
											selectedDomain?.id === domain.id
												? 'border-primary-500 bg-primary-50'
												: 'border-gray-200 hover:border-gray-300'
										}`}
										onClick={() => setSelectedDomain(domain)}
									>
										<div className="flex items-center justify-between">
											<div className="flex items-center space-x-3">
												<Globe className="w-5 h-5 text-gray-500" />
												<div>
													<Typography variant="body1" className="font-medium">
														{domain.domain}
													</Typography>
													<Typography variant="body2" color="muted">
														{domain.domain_type}{' '}
														{domain.is_primary && '(Primary)'}
													</Typography>
												</div>
											</div>
											<div className="flex items-center space-x-2">
												<div
													className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
														domain.verification_status,
													)}`}
												>
													{getStatusIcon(domain.verification_status)}
													<span className="ml-1 capitalize">
														{domain.verification_status}
													</span>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>

						{/* DNS Instructions for Custom Domains */}
						{selectedDomain && selectedDomain.domain_type === 'custom' && (
							<div className="mt-6 p-4 bg-gray-50 rounded-lg">
								<Typography variant="h3" className="mb-4">
									DNS Setup Instructions
								</Typography>

								{instructionsLoading ? (
									<Typography variant="body2" color="muted">
										Loading instructions...
									</Typography>
								) : instructionsData ? (
									<div className="space-y-4">
										{/* CNAME Record */}
										<div className="bg-white p-4 rounded border">
											<Typography variant="body2" className="font-medium mb-2">
												Step 1: Add CNAME Record
											</Typography>
											<Typography
												variant="body2"
												color="muted"
												className="mb-3"
											>
												{instructionsData.instructions.step1}
											</Typography>
											<div className="bg-gray-50 p-3 rounded font-mono text-sm space-y-1">
												<div className="flex items-center justify-between">
													<span className="text-gray-600">Name:</span>
													<div className="flex items-center space-x-2">
														<span>{instructionsData.cname_record.name}</span>
														<button
															onClick={() =>
																handleCopy(instructionsData.cname_record.name)
															}
															className="text-primary-600 hover:text-primary-800"
														>
															<Copy className="w-4 h-4" />
														</button>
													</div>
												</div>
												<div className="flex items-center justify-between">
													<span className="text-gray-600">Type:</span>
													<span>{instructionsData.cname_record.type}</span>
												</div>
												<div className="flex items-center justify-between">
													<span className="text-gray-600">Value:</span>
													<div className="flex items-center space-x-2">
														<span>{instructionsData.cname_record.value}</span>
														<button
															onClick={() =>
																handleCopy(instructionsData.cname_record.value)
															}
															className="text-primary-600 hover:text-primary-800"
														>
															<Copy className="w-4 h-4" />
														</button>
													</div>
												</div>
												<div className="flex items-center justify-between">
													<span className="text-gray-600">TTL:</span>
													<span>{instructionsData.cname_record.ttl}</span>
												</div>
											</div>
										</div>

										{/* TXT Record */}
										<div className="bg-white p-4 rounded border">
											<Typography variant="body2" className="font-medium mb-2">
												Step 2: Add TXT Record for Verification
											</Typography>
											<Typography
												variant="body2"
												color="muted"
												className="mb-3"
											>
												{instructionsData.instructions.step2}
											</Typography>
											<div className="bg-gray-50 p-3 rounded font-mono text-sm space-y-1">
												<div className="flex items-center justify-between">
													<span className="text-gray-600">Name:</span>
													<div className="flex items-center space-x-2">
														<span>{instructionsData.txt_record.name}</span>
														<button
															onClick={() =>
																handleCopy(instructionsData.txt_record.name)
															}
															className="text-primary-600 hover:text-primary-800"
														>
															<Copy className="w-4 h-4" />
														</button>
													</div>
												</div>
												<div className="flex items-center justify-between">
													<span className="text-gray-600">Type:</span>
													<span>{instructionsData.txt_record.type}</span>
												</div>
												<div className="flex items-center justify-between">
													<span className="text-gray-600">Value:</span>
													<div className="flex items-center space-x-2">
														<span className="break-all">
															{instructionsData.txt_record.value}
														</span>
														<button
															onClick={() =>
																handleCopy(instructionsData.txt_record.value)
															}
															className="text-primary-600 hover:text-primary-800"
														>
															<Copy className="w-4 h-4" />
														</button>
													</div>
												</div>
												<div className="flex items-center justify-between">
													<span className="text-gray-600">TTL:</span>
													<span>{instructionsData.txt_record.ttl}</span>
												</div>
											</div>
										</div>

										{/* Note */}
										<Alert variant="info" className="mt-4">
											<Typography variant="body2">
												<strong>Note:</strong>{' '}
												{instructionsData.instructions.note}
											</Typography>
										</Alert>

										{/* Verify Button */}
										{selectedDomain.verification_status !== 'verified' && (
											<Button
												onClick={handleVerify}
												disabled={verifying}
												loading={verifying}
												variant="primary"
												className="w-full"
											>
												Verify DNS Records
											</Button>
										)}
									</div>
								) : (
									<Alert variant="warning">
										Failed to load DNS instructions. Please try again.
									</Alert>
								)}
							</div>
						)}
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				<Button onClick={onClose} variant="secondary">
					Close
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default TenantDomainsModal;
